import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("access_token")}` });

const defaultWhen = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const STATUS = {
  scheduled: ["⏳ In programma", "text-amber-700 bg-amber-50"],
  sending: ["📤 Invio…", "text-sky-700 bg-sky-50"],
  sent: ["✅ Pubblicato", "text-green-700 bg-green-50"],
  failed: ["❌ Fallito", "text-red-700 bg-red-50"],
};

/** Schedule a channel post (text and/or photo) for a future date/time. */
export const TelegramScheduledPosts = () => {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [when, setWhen] = useState(defaultWhen);
  const [weekly, setWeekly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/telegram/scheduled-posts`, { headers: authHeaders() });
      setPosts(res.data.posts || []);
    } catch (e) {
      console.warn("[telegram-scheduled] load failed:", e?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const schedule = async () => {
    if (busy || (!text.trim() && !photo) || !when) return;
    setBusy(true);
    setResult("");
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      fd.append("scheduled_at", new Date(when).toISOString());
      if (photo) fd.append("photo", photo);
      if (weekly) fd.append("repeat", "weekly");
      await axios.post(`${BACKEND_URL}/api/admin/telegram/scheduled-posts`, fd, { headers: authHeaders() });
      setResult(`✅ Programmato per il ${new Date(when).toLocaleString("it-IT")}${weekly ? " · si ripete ogni settimana" : ""}`);
      setText("");
      setPhoto(null);
      setWeekly(false);
      await load();
    } catch (e) {
      setResult(`❌ ${e.response?.data?.detail || "Errore"}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Annullare questo post programmato?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/telegram/scheduled-posts/${id}`, { headers: authHeaders() });
      await load();
    } catch (e) {
      setResult(`❌ ${e.response?.data?.detail || "Errore"}`);
    }
  };

  return (
    <div className="mt-4 border-t pt-4" data-testid="telegram-scheduled-section">
      <p className="text-xs font-semibold text-gray-600 mb-2">🗓️ Programma un post sul canale</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        maxLength={4000}
        placeholder="Es. Sabato mattina arrivano i nuovi camion Scania: passate in edicola!"
        className="w-full text-sm border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#229ED9]/40"
        data-testid="telegram-schedule-textarea"
      />
      <div className="flex items-end gap-3 mt-2 flex-wrap">
        <label className="text-[11px] text-gray-500">Data e ora
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
            className="block text-sm border rounded-lg px-2 py-1.5 mt-0.5" data-testid="telegram-schedule-datetime" />
        </label>
        <label className="cursor-pointer text-xs font-semibold text-gray-600 border border-dashed border-gray-300 hover:border-[#229ED9] rounded-lg px-3 py-2 transition-colors">
          📷 {photo ? photo.name.slice(0, 20) : "Allega foto"}
          <input type="file" accept="image/*" className="hidden" data-testid="telegram-schedule-photo-input"
            onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
        </label>
        {photo && (
          <button onClick={() => setPhoto(null)} className="text-red-500 text-xs font-bold" data-testid="telegram-schedule-photo-remove">✕ rimuovi foto</button>
        )}
        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer py-2" data-testid="telegram-schedule-weekly-label">
          <input type="checkbox" checked={weekly} onChange={(e) => setWeekly(e.target.checked)} data-testid="telegram-schedule-weekly-checkbox" />
          🔁 Ripeti ogni settimana
        </label>
        <button onClick={schedule} disabled={busy || (!text.trim() && !photo) || !when}
          className="bg-[#229ED9] hover:bg-[#1c86b8] disabled:opacity-40 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors"
          data-testid="telegram-schedule-submit-btn">
          {busy ? "Salvo…" : "🗓️ Programma"}
        </button>
      </div>
      {result && <p className="text-xs font-semibold mt-2" data-testid="telegram-schedule-result">{result}</p>}

      {posts.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="telegram-scheduled-list">
          {posts.slice(0, 8).map((p) => {
            const [label, cls] = STATUS[p.status] || STATUS.scheduled;
            return (
              <li key={p.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-2.5 py-1.5" data-testid={`telegram-scheduled-item-${p.id}`}>
                <span className={`px-1.5 py-0.5 rounded font-bold whitespace-nowrap ${cls}`}>{label}</span>
                {p.repeat === "weekly" && (
                  <span className="px-1.5 py-0.5 rounded font-bold whitespace-nowrap text-violet-700 bg-violet-50" title={`Inviato ${p.times_sent || 0} volte`} data-testid={`telegram-scheduled-weekly-${p.id}`}>
                    🔁 settimanale{p.times_sent ? ` ×${p.times_sent}` : ""}
                  </span>
                )}
                <span className="text-gray-500 whitespace-nowrap">{new Date(p.scheduled_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
                <span className="text-gray-700 truncate flex-1" title={p.text}>{p.article_id ? "🛍️ " : p.has_photo ? "📷 " : ""}{p.text || "(solo foto)"}</span>
                {p.status !== "sending" && (
                  <button onClick={() => remove(p.id)} className="text-gray-400 hover:text-red-500 font-bold" title={p.status === "scheduled" ? "Annulla" : "Rimuovi dallo storico"} data-testid={`telegram-scheduled-delete-${p.id}`}>✕</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
